import React, { useEffect, useState, useCallback } from "react";
import {
  colors_from_image_wrapper,
  empty_state,
  extracted_colors,
  color_swatch,
  palette_actions,
  action_btn,
  toast,
} from "../../../styles/modules/palette_from_image.module.css";
import ColorThief from "colorthief";
import chroma from "chroma-js";
import { darkOrLightColor } from "../../../helpers/darkOrLightColor";

// Textos multiidioma
const TEXTS = {
  es: {
    extractedColors: "Colores extraídos",
    emptyTitle: "Sin imagen",
    emptyText: "Sube una imagen para extraer su paleta de colores",
    copyAll: "Copiar todos",
    copyCSS: "Copiar CSS",
    newImage: "Nueva imagen",
    copied: "¡Copiado!",
  },
  en: {
    extractedColors: "Extracted colors",
    emptyTitle: "No image",
    emptyText: "Upload an image to extract its color palette",
    copyAll: "Copy all",
    copyCSS: "Copy CSS",
    newImage: "New image",
    copied: "Copied!",
  },
  pt: {
    extractedColors: "Cores extraídas",
    emptyTitle: "Sem imagem",
    emptyText: "Carregue uma imagem para extrair sua paleta de cores",
    copyAll: "Copiar todos",
    copyCSS: "Copiar CSS",
    newImage: "Nova imagem",
    copied: "Copiado!",
  },
  fr: {
    extractedColors: "Couleurs extraites",
    emptyTitle: "Pas d'image",
    emptyText: "Téléchargez une image pour extraire sa palette de couleurs",
    copyAll: "Copier tout",
    copyCSS: "Copier CSS",
    newImage: "Nouvelle image",
    copied: "Copié!",
  },
  de: {
    extractedColors: "Extrahierte Farben",
    emptyTitle: "Kein Bild",
    emptyText: "Lade ein Bild hoch, um seine Farbpalette zu extrahieren",
    copyAll: "Alle kopieren",
    copyCSS: "CSS kopieren",
    newImage: "Neues Bild",
    copied: "Kopiert!",
  },
};

function ColorsFromImage({ images = [], locale, onClear }) {
  const texts = TEXTS[locale] || TEXTS.es;
  const [palette, setPalette] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Extraer colores de la imagen
  useEffect(() => {
    if (images.length === 0) {
      setPalette([]);
      return;
    }

    const colorThief = new ColorThief();
    const img = document.createElement("img");
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      try {
        const colors = colorThief.getPalette(img, 6);
        const formatPalette = colors.map(color =>
          chroma(`rgb(${color[0]}, ${color[1]}, ${color[2]})`)
            .hex()
            .toUpperCase()
        );
        setPalette(formatPalette);
      } catch (error) {
        console.error("Error extracting colors:", error);
      }
    };

    img.src = images[0].src;
  }, [images]);

  // Mostrar toast
  const displayToast = useCallback(message => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, []);

  // Copiar color individual
  const copyColor = useCallback(
    color => {
      navigator.clipboard.writeText(color);
      displayToast(`${texts.copied} ${color}`);
    },
    [displayToast, texts.copied]
  );

  // Copiar todos los colores
  const copyAllColors = useCallback(() => {
    const colorsText = palette.join(", ");
    navigator.clipboard.writeText(colorsText);
    displayToast(texts.copied);
  }, [palette, displayToast, texts.copied]);

  // Copiar como CSS
  const copyCSSVariables = useCallback(() => {
    const css = `:root {\n${palette
      .map((c, i) => `  --color-${i + 1}: ${c};`)
      .join("\n")}\n}`;
    navigator.clipboard.writeText(css);
    displayToast(texts.copied);
  }, [palette, displayToast, texts.copied]);

  // Estado vacío
  if (images.length === 0) {
    return (
      <div className={colors_from_image_wrapper}>
        <div className={empty_state}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <p>
            <strong>{texts.emptyTitle}</strong>
          </p>
          <p>{texts.emptyText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={colors_from_image_wrapper}>
      <h2>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="13.5" cy="6.5" r="2.5" />
          <circle cx="17.5" cy="10.5" r="2.5" />
          <circle cx="8.5" cy="7.5" r="2.5" />
          <circle cx="6.5" cy="12.5" r="2.5" />
          <path d="M12 22c-4.97 0-9-2.582-9-7v-.088C3 12.794 4.338 11.1 6.375 10c1.949-1.052 3.101-2.99 2.813-5l-.563-3 2.086.795c.64.244 1.064.858 1.064 1.54v.665c0 1.657 1.343 3 3 3h.225c1.553 0 2.775 1.3 2.775 2.85v.65c0 1.657 1.343 3 3 3H22c-.663 2.393-3.006 4.5-6 4.5h-4z" />
        </svg>
        {texts.extractedColors}
      </h2>

      {/* Colores extraídos */}
      <div className={extracted_colors}>
        {palette.map((color, i) => (
          <div
            key={`${color}-${i}`}
            className={color_swatch}
            style={{ backgroundColor: color, color: darkOrLightColor(color) }}
            onClick={() => copyColor(color)}
            title={color}
          >
            <span>{color.slice(1)}</span>
          </div>
        ))}
      </div>

      {/* Acciones */}
      <div className={palette_actions}>
        <button className={action_btn} onClick={copyAllColors}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          {texts.copyAll}
        </button>
        <button className={action_btn} onClick={copyCSSVariables}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
          {texts.copyCSS}
        </button>
        <button className={action_btn} onClick={onClear}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M23 4v6h-6" />
            <path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          {texts.newImage}
        </button>
      </div>

      {/* Toast */}
      {showToast && (
        <div className={toast}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ width: 18, height: 18 }}
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {toastMessage}
        </div>
      )}
    </div>
  );
}

export default ColorsFromImage;
